import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { FILE_UPLOAD_STATUS } from '@graphql/fileUploadStatus.gql';
import { message } from 'antd';
import { formatToAtlanticTime } from '../../utils/dateFormatter';

export const useFileUploadStatus = (fileIngestionId) => {
  const [pageVars, setPageVars] = useState({
    page: 1,
    limit: 25,
    orderBy: 'START_TIME',
    sort: 'ASC',
  });

  const { data, loading, error } = useQuery(FILE_UPLOAD_STATUS, {
    variables: {
      fileIngestionId: Number.parseInt(fileIngestionId),
      ...pageVars,
    },
    fetchPolicy: 'cache-and-network',
    skip: !fileIngestionId,
  });

  useEffect(() => {
    if (error) {
      message.error(error.message || 'Failed to fetch file upload status');
    }
  }, [error]);

  // Enhanced setParams to match RemoteDataTable's expected interface
  const setParams = useCallback((params) => {
    setPageVars((prev) => ({
      ...prev,
      page: params.page ?? prev.page,
      limit: params.limit ?? prev.limit,
      orderBy: params.orderBy ?? prev.orderBy,
      sort: params.sort ?? prev.sort,
    }));
  }, []);

  const transformedData = (data?.fileUploadStatus?.nodes ?? []).map((record) => ({
    // Essential fields for component functionality
    id: `${record.runId}-${record.subTaskName || 'main'}-${record.startTime}`, // Unique row key
    runId: record.runId,
    fileIngestionId: record.fileIngestionId,
    filename: record.filename,
    parentJobName: record.parentJobName,
    subTaskName: record.subTaskName,

    // Formatted time and duration columns
    startTimeFormatted: formatToAtlanticTime(record.startTime, 'full'),
    endTimeFormatted: record.endTime ? formatToAtlanticTime(record.endTime, 'full') : null,
    durationFormatted: record.duration ? `${record.duration}s` : null,

    // Status and level
    level: record.level,
    status: record.status,
  }));

  // Return interface compatible with RemoteDataTable
  return {
    data: transformedData,
    totalCount: data?.fileUploadStatus?.totalCount ?? 0,
    loading,
    error,
    setError: () => { }, // RemoteDataTable expects this
    setParams,
  };
};
